import useNavigationMenu from '@Guidelines/Shell/AppShell/Hooks/useNavigationMenu';
import type {
	NavigationMenuDestination,
	NavigationMenuEntry,
	NavigationMenuSectionId,
} from '@UI/Routing/navigationMenu';
import useBeneficiaryAccess from '@UI/Hooks/useBeneficiaryAccess';
import useUserPermissions from '@UI/Providers/PermissionProvider/useUserPermissions';
import * as React from 'react';

export type VisibleNavigationEntry = {
	readonly entry: NavigationMenuEntry;
	/** First accessible destination's path. */
	readonly href: string;
};

export type VisibleNavigationSection = {
	readonly id: NavigationMenuSectionId;
	readonly label: string;
	readonly entries: readonly VisibleNavigationEntry[];
};

/**
 * `[]` until permissions are resolved and the subject types are fetched; sections and entries without access are dropped. Without
 * `sectionIds` the whole menu is returned, otherwise only those sections, in catalogue order.
 * `sectionIds` must be a stable reference (a module constant).
 */
const useVisibleNavigationMenu = (
	sectionIds?: readonly NavigationMenuSectionId[],
): readonly VisibleNavigationSection[] => {
	const { isResolved, hasAnyPermission } = useUserPermissions();
	const { menu, isLoading } = useNavigationMenu();
	const canReadBeneficiaries = useBeneficiaryAccess().access?.canRead === true;

	return React.useMemo(() => {
		if (!isResolved || isLoading) {
			return [];
		}

		const isAccessible = (destination: NavigationMenuDestination): boolean => {
			if (destination.requiresBeneficiaryRead === true) {
				return canReadBeneficiaries;
			}

			return destination.permissions.length === 0 || hasAnyPermission(destination.permissions);
		};

		const sections =
			sectionIds === undefined ? menu : menu.filter((section) => sectionIds.includes(section.id));

		return sections
			.map((section) => {
				const entries = section.entries.flatMap((entry): VisibleNavigationEntry[] => {
					const destination = entry.destinations.find(isAccessible);

					return destination === undefined ? [] : [{ entry, href: destination.path }];
				});

				return { id: section.id, label: section.label, entries };
			})
			.filter((section) => section.entries.length > 0);
	}, [isResolved, isLoading, hasAnyPermission, canReadBeneficiaries, menu, sectionIds]);
};

export default useVisibleNavigationMenu;
